import { useState, useEffect } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { CheckCircle, Send } from "lucide-react";

const initial = {
    first_name: "", last_name: "", organization: "", job_title: "",
    email: "", phone: "", country: "", product: "procurement", org_size: "",
    preferred_date: "", preferred_time: "", message: "",
};

export default function RequestDemo() {
    const [form, setForm] = useState(initial);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [ref, setRef] = useState(null);

    useEffect(() => { api.get("/products").then((r) => setProducts(r.data.products)); }, []);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/demo-requests", form);
            setRef(data.reference);
            toast.success(`Demo request received — ${data.reference}`);
            setForm(initial);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Something went wrong");
        }
        setLoading(false);
    };

    if (ref) {
        return (
            <main className="pt-[72px] min-h-screen bg-[#0B1930] text-white">
                <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-32 text-center">
                    <CheckCircle className="w-16 h-16 text-[#E8B923] mx-auto" />
                    <div className="eyebrow mt-8">Confirmed</div>
                    <h1 className="headline text-5xl lg:text-6xl mt-4">Your demo is on the way.</h1>
                    <p className="text-white/70 mt-6 max-w-lg mx-auto">A Colecle specialist will reach out within one business day to schedule your personalized walkthrough.</p>
                    <div className="mt-10 inline-block border border-white/20 rounded-full px-6 py-3 font-mono text-sm text-[#E8B923]">
                        Reference · {ref}
                    </div>
                </div>
            </main>
        );
    }

    return (
        <main data-testid="request-demo-page" className="pt-[72px]">
            <section className="bg-[#0B1930] text-white pt-24 pb-16 relative overflow-hidden grain">
                <div className="hero-glow top-[-30%] right-[10%]" style={{ background: "#2563EB" }} />
                <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
                    <div className="eyebrow">Request a Demo</div>
                    <h1 className="headline text-5xl md:text-6xl lg:text-7xl mt-6 max-w-3xl">
                        Book a personalized <em className="not-italic text-[#E8B923]">walkthrough.</em>
                    </h1>
                    <p className="text-white/70 text-lg mt-6 max-w-2xl">
                        Tell us a bit about your organization. Our team will tailor the demo to your industry, workflows and technical requirements.
                    </p>
                </div>
            </section>
            <section className="py-20 max-w-[1100px] mx-auto px-6 lg:px-10">
                <form onSubmit={submit} className="grid md:grid-cols-2 gap-4 bg-white">
                    <Field label="First Name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="demo-first-name" required />
                    <Field label="Last Name" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testid="demo-last-name" required />
                    <Field label="Organization" value={form.organization} onChange={(v) => setForm({ ...form, organization: v })} testid="demo-org" required />
                    <Field label="Job Title" value={form.job_title} onChange={(v) => setForm({ ...form, job_title: v })} testid="demo-title" />
                    <Field label="Business Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="demo-email" required />
                    <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="demo-phone" />
                    <Field label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="demo-country" />
                    <div className="flex flex-col gap-2">
                        <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Product of Interest *</label>
                        <select data-testid="demo-product" required value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} className="border border-slate-200 rounded-md px-4 py-3 bg-white">
                            {products.map((p) => <option key={p.slug} value={p.slug}>{p.name}</option>)}
                        </select>
                    </div>
                    <div className="flex flex-col gap-2">
                        <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Organization Size</label>
                        <select data-testid="demo-size" value={form.org_size} onChange={(e) => setForm({ ...form, org_size: e.target.value })} className="border border-slate-200 rounded-md px-4 py-3 bg-white">
                            <option value="">Select…</option>
                            <option>1-10</option>
                            <option>11-50</option>
                            <option>51-200</option>
                            <option>201-1000</option>
                            <option>1000+</option>
                        </select>
                    </div>
                    <Field label="Preferred Date" type="date" value={form.preferred_date} onChange={(v) => setForm({ ...form, preferred_date: v })} testid="demo-date" />
                    <Field label="Preferred Time" type="time" value={form.preferred_time} onChange={(v) => setForm({ ...form, preferred_time: v })} testid="demo-time" />
                    <div className="flex flex-col gap-2 md:col-span-2">
                        <label className="text-xs font-mono uppercase tracking-widest text-slate-500">Message</label>
                        <textarea data-testid="demo-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="border border-slate-200 rounded-md px-4 py-3 bg-white" placeholder="Tell us about your priorities and use cases…" />
                    </div>
                    <div className="md:col-span-2 flex justify-end">
                        <button data-testid="demo-submit" disabled={loading} className="btn-primary">
                            {loading ? "Sending…" : "Request Demo"} <Send className="w-4 h-4" />
                        </button>
                    </div>
                </form>
            </section>
        </main>
    );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
    return (
        <div className="flex flex-col gap-2">
            <label className="text-xs font-mono uppercase tracking-widest text-slate-500">{label}{required && " *"}</label>
            <input data-testid={testid} type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="border border-slate-200 rounded-md px-4 py-3 bg-white" />
        </div>
    );
}
